/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { avm2AwayDepth, away2avmDepth, getAVM1Object, getAwayObjectOrTemplate, hasAwayJSAdaptee, initializeAVM1Object, toTwipFloor, wrapAVM1NativeClass, } from './AVM1Utils';
import { alCoerceString, alForEachProperty, alIsArray, alIsName, alNewObject, alToBoolean, alToInt32, alToNumber, alToString, } from '../runtime';
import { assert, Debug, isNullOrUndefined, release, somewhatImplemented, warning, } from '@awayfl/swf-loader';
import { toAS3BitmapData } from './AVM1BitmapData';
import { toAS3Matrix } from './AVM1Matrix';
import { AVM1ArrayNative } from '../natives';
import { copyAS3PointTo, toAS3Point } from './AVM1Point';
import { MovieClipProperties } from '../interpreter/MovieClipProperties';
import { FrameScriptManager, MouseEvent as AwayMouseEvent, MovieClip, TextField, } from '@awayjs/scene';
import { AssetLibrary, Point, Rectangle, WaveAudio, } from '@awayjs/core';
import { AVM1TextField } from './AVM1TextField';
import { LineScaleMode } from '@awayjs/renderer';
import { AVM1SymbolBase } from './AVM1SymbolBase';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Stage } from './AVM1Stage';
import { EventsListForMC } from './AVM1EventHandler';
import { AVM1LoaderHelper } from './AVM1LoaderHelper';
import { AVM1Globals } from './AVM1Globals';
function convertAS3RectangeToBounds(as3Rectange, context) {
    var result = alNewObject(context);
    result.alPut('xMin', as3Rectange.left);
    result.alPut('yMin', as3Rectange.top);
    result.alPut('xMax', as3Rectange.right);
    result.alPut('yMax', as3Rectange.bottom);
    return result;
}
var AVM1MovieClip = /** @class */ (function (_super) {
    __extends(AVM1MovieClip, _super);
    function AVM1MovieClip() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._hitReportCount = 0;
        _this._depthToChilds = {};
        // should be a 0, but it return a -1 when there are not childrens
        _this._nextHighestDepth = 1;
        _this.executeConstructor = null;
        /**
         * Lock object of `unregister` method. Required for AVM1BtimapData.draw
         */
        /*internal*/ _this._locked = false;
        _this.unregisteredColors = {};
        _this.isDragging = false;
        _this.startDragPoint = new Point();
        _this.startDragMCPosition = new Point();
        return _this;
    }
    AVM1MovieClip.createAVM1Class = function (context) {
        return wrapAVM1NativeClass(context, true, AVM1MovieClip, [], ['$version#', '_alpha#', 'getAwayJSID', 'attachAudio', 'attachBitmap', 'attachMovie',
            'attachVideo',
            'beginFill', 'beginBitmapFill', 'beginGradientFill', 'blendMode#',
            'cacheAsBitmap#', '_callFrame', 'clear', 'createEmptyMovieClip',
            'createTextField', '_currentframe#', 'curveTo', '_droptarget#',
            'duplicateMovieClip', 'enabled#', 'endFill', 'filters#', '_framesloaded#',
            '_focusrect#', 'forceSmoothing#', 'getBounds',
            'getBytesLoaded', 'getBytesTotal', 'getDepth', 'getInstanceAtDepth',
            'getNextHighestDepth', 'getRect', 'getSWFVersion', 'getTextSnapshot',
            'getURL', 'globalToLocal', 'gotoAndPlay', 'gotoAndStop', '_height#',
            '_highquality#', 'hitArea#', 'hitTest', 'lineGradientStyle', 'lineStyle',
            'lineTo', 'loadMovie', 'loadVariables', 'localToGlobal', '_lockroot#',
            'menu#', 'moveTo', '_name#', 'nextFrame', 'opaqueBackground#', '_parent#',
            'play', 'prevFrame', '_quality#', 'removeMovieClip', '_root#', '_rotation#',
            'scale9Grid#', 'scrollRect#', 'setMask', '_soundbuftime#', 'startDrag',
            'stop', 'stopDrag', 'swapDepths', 'tabChildren#', 'tabEnabled#', 'tabIndex#',
            '_target#', '_totalframes#', 'trackAsMenu#', 'transform#', 'toString',
            'unloadMovie', '_url#', 'useHandCursor#', '_visible#', '_width#',
            '_x#', '_xmouse#', '_xscale#', '_y#', '_ymouse#', '_yscale#']);
    };
    Object.defineProperty(AVM1MovieClip.prototype, "_stage", {
        get: function () {
            return AVM1Stage.avmStage;
        },
        enumerable: false,
        configurable: true
    });
    /*internal*/ AVM1MovieClip.prototype.setDepthToChild = function (depth, child) {
        if (!child) {
            delete this._depthToChilds[depth];
            //console.debug('[AVM1 MC]', this.adaptee.id, ' remove child at depth:', depth);
            return;
        }
        this._depthToChilds[depth] = child;
        //console.debug('[AVM1 MC]', this.adaptee.id, ' add child at depth:', depth);
    };
    /*internal*/ AVM1MovieClip.prototype.getDepthToChild = function (depth) {
        var child = this._depthToChilds[depth];
        //console.debug('[AVM1 MC]', this.adaptee.id, ' get child at depth:', depth, !!child);
        return child;
    };
    AVM1MovieClip.prototype.addTimelineChildAtDepth = function (child, depth) {
        depth -= 16383;
        if (child.adapter != child && child.adapter.deleteOwnProperties) {
            child.adapter.deleteOwnProperties();
        }
        child.reset();
        this.addChildAtDepth(child, depth, true);
    };
    AVM1MovieClip.prototype.removeTimelineChildAt = function (value) {
        // avm1 supplied value is depth
        value -= 16383;
        this.removeChildAtDepth(value);
    };
    AVM1MovieClip.prototype.removeAllTimelineChilds = function () {
        for (var i = this.adaptee.numChildren - 1; i >= 0; i--) {
            var child = this.adaptee.getChildAt(i);
            if (child._avmDepthID < 0)
                this.adaptee.removeChildAt(i);
        }
    };
    AVM1MovieClip.prototype.getTimelineChildAtSessionID = function (sessionID) {
        return this.adaptee.getTimelineChildAtSessionID(sessionID);
    };
    AVM1MovieClip.prototype.getDepthIndexInternal = function (depth) {
        var child = this.getDepthToChild(depth);
        if (!child) {
            return -1;
        }
        // getChildIndex us throwable when parnet is not valid
        if (child.parent !== this.adaptee) {
            return -1;
        }
        return this.adaptee.getChildIndex(child);
    };
    AVM1MovieClip.prototype.getIndexFromDepthOrTop = function (depth) {
        for (var i = 0; i < this.adaptee.numChildren; i++) {
            if (this.adaptee.getChildAt(i)._avmDepthID > depth)
                return i;
        }
        // return top index when a depth not exist ellement with depth greater a requested
        return this.adaptee.numChildren;
    };
    AVM1MovieClip.prototype.removeChildAtDepth = function (depth) {
        var idx = this.getDepthIndexInternal(depth);
        if (idx !== -1) {
            this.removeChildAt(idx);
        }
    };
    AVM1MovieClip.prototype.removeChildAt = function (index) {
        var child = this.adaptee.removeChildAt(index);
        if (child._adapter)
            child.adapter.freeFromScript();
        this.unregisterScriptObject(child);
        delete this.adaptee._sessionID_childs[child._sessionID];
        //delete this._depthToChilds[child._avmDepthID];
        this.setDepthToChild(child._avmDepthID);
        child._sessionID = -1;
        child._avmDepthID = -16384;
    };
    AVM1MovieClip.prototype.addChildAtDepth = function (child, depth, fromTimeline) {
        //console.log("[AVM1MovieClip]", this.adaptee.name, "addChildAtDepth", child, depth, fromTimeline);
        if (fromTimeline === void 0) { fromTimeline = false; }
        // if depth is already occupied:
        //		- when addChild is invoke from script: replace existing object
        //		- when addChild is invoked from timeline: return and do nothing
        if (child == null)
            throw ('Parameter child cannot be null.');
        var index = this.getDepthIndexInternal(depth);
        if (index != -1) {
            if (fromTimeline) {
                console.log('depth is occupied');
                return null;
            }
            this.removeChildAt(index);
            this.adaptee.addChildAt(child, index);
        }
        else {
            this.adaptee.addChildAt(child, this.getIndexFromDepthOrTop(depth));
        }
        if (this.adaptee.isSlice9ScaledMC && child.assetType == '[asset Sprite]') {
            child.isSlice9ScaledSprite = true;
        }
        if (this._nextHighestDepth < depth + 1)
            this._nextHighestDepth = depth + 1;
        child._avmDepthID = depth;
        if (child.adapter != child) {
            child.adapter.initAdapter();
        }
        this.setDepthToChild(depth, child);
        //this._depthToChilds[depth] = child;
        if (fromTimeline) {
            this.adaptee._sessionID_childs[child._sessionID] = child;
        }
        var avmChild = getAVM1Object(child, this._avm1Context);
        // so, we should register node at this place too
        // because timeline not register child before update_child,
        // but it not always can be called.
        this.registerScriptObject(child, fromTimeline);
        return avmChild;
    };
    AVM1MovieClip.prototype.sortVirtualSceneGraph = function (a, b) {
        return a.depth - b.depth;
    };
    /**
     * queue the framescripts for a specific frame
     * @param timeline
     * @param frame_idx
     * @param scriptPass1
     */
    AVM1MovieClip.prototype.queueFrameScripts = function (timeline, frame_idx, scriptPass1) {
        var frameScripts = timeline.get_script_for_frame(this.adaptee, frame_idx, true);
        if (frameScripts) {
            //console.log("add framescript", target_mc, target_mc.name, keyframe_idx, scriptPass1 );
            if (scriptPass1)
                FrameScriptManager.add_script_to_queue(this.adaptee, frameScripts);
            else
                FrameScriptManager.add_script_to_queue_pass2(this.adaptee, frameScripts);
        }
    };
    // should only be called from timeline when navigating frames
    AVM1MovieClip.prototype.constructFrame = function (timeline, start_construct_idx, target_keyframe_idx, jump_forward, frame_idx, queue_pass2, queue_script) {
        var virtualSceneGraphMap = {};
        var existingSessionIDs = {};
        // step1: prepare virtual-scenegraph:
        // collect existing children into a virtual-scenegraph
        // if we jump forward, we collect all children
        // if we jump back, we only collect children with depth > 0 (usually added via script)
        var len = this.adaptee.numChildren;
        for (var i_1 = 0; i_1 < len; i_1++) {
            var child = this.adaptee.getChildAt(i_1);
            if (jump_forward || child._sessionID == -1) {
                virtualSceneGraphMap[child._avmDepthID] = {
                    sessionID: child._sessionID,
                    addedOnTargetFrame: false,
                    depth: child._avmDepthID,
                    child: child
                };
            }
            if (child._sessionID >= 0)
                existingSessionIDs[child._sessionID] = child;
        }
        // step2: apply add/remove commands into virtual-scenegraph
        var i;
        var k;
        timeline._update_indices.length = 0;
        timeline._update_frames.length = 0;
        var update_cnt = 0;
        var start_index;
        var end_index;
        for (k = start_construct_idx; k <= target_keyframe_idx; k++) {
            var frame_command_idx = timeline.frame_command_indices[k];
            var frame_recipe = timeline.frame_recipe[k];
            if (frame_recipe & 2) {
                // remove childs by depth. no matter what object is at the depth, it gets removed
                start_index = timeline.command_index_stream[frame_command_idx];
                end_index = start_index + timeline.command_length_stream[frame_command_idx++];
                for (i = start_index; i < end_index; i++) {
                    delete virtualSceneGraphMap[timeline.remove_child_stream[i] - 16383];
                }
            }
            if (frame_recipe & 4) {
                start_index = timeline.command_index_stream[frame_command_idx];
                end_index = start_index + timeline.command_length_stream[frame_command_idx++];
                if (queue_pass2) {
                    for (i = end_index - 1; i >= start_index; i--) {
                        // in as2 only want to add childs if the depth is free
                        if (!virtualSceneGraphMap[timeline.add_child_stream[i * 3 + 1] - 16383]) {
                            virtualSceneGraphMap[timeline.add_child_stream[i * 3 + 1] - 16383] = {
                                sessionID: timeline.add_child_stream[i * 3],
                                symbolID: timeline.add_child_stream[i * 3 + 2],
                                addedOnTargetFrame: k == target_keyframe_idx,
                                depth: timeline.add_child_stream[i * 3 + 1] - 16383
                            };
                        }
                    }
                }
                else {
                    for (i = start_index; i < end_index; i++) {
                        if (!virtualSceneGraphMap[timeline.add_child_stream[i * 3 + 1] - 16383]) {
                            virtualSceneGraphMap[timeline.add_child_stream[i * 3 + 1] - 16383] = {
                                sessionID: timeline.add_child_stream[i * 3],
                                symbolID: timeline.add_child_stream[i * 3 + 2],
                                addedOnTargetFrame: k == target_keyframe_idx,
                                depth: timeline.add_child_stream[i * 3 + 1] - 16383
                            };
                        }
                    }
                }
            }
            if (frame_recipe & 8) {
                timeline._update_frames[update_cnt] = timeline.keyframe_firstframes[k];
                timeline._update_indices[update_cnt++] = frame_command_idx++;
            }
            if (frame_recipe & 16 && k == target_keyframe_idx) {
                timeline.start_sounds(this.adaptee, frame_command_idx);
            }
        }
        // step3: sort virtual scenegraph by depth
        var virtualSceneGraph = [];
        for (var key in virtualSceneGraphMap) {
            virtualSceneGraph[virtualSceneGraph.length] = virtualSceneGraphMap[key];
        }
        virtualSceneGraph.sort(this.sortVirtualSceneGraph);
        var newChildren = [];
        var vsItem;
        var newChilds = [];
        var newChildsOnTargetFrame = [];
        this._depthToChilds = {};
        this.adaptee._sessionID_childs = {};
        // step4: compare virtual scenegraph against current children
        // - if child exists in both, and has same session id, we keep it alive
        // - if it is a new child, we create new instance
        // for new children that was not added on target-frame, we prevent framescripts
        this.adaptee.preventScript = true;
        len = newChildren.length = virtualSceneGraph.length;
        for (var i_2 = 0; i_2 < len; i_2++) {
            vsItem = virtualSceneGraph[i_2];
            if (vsItem.sessionID == -1 && vsItem.child) {
                newChildren[i_2] = vsItem.child;
                this.setDepthToChild(vsItem.depth, vsItem.child);
            }
            else if (existingSessionIDs[vsItem.sessionID]) {
                //	the same sessionID already is child of the mc
                var existingChild = existingSessionIDs[vsItem.sessionID];
                var depth = vsItem.depth;
                //	set existing child to correct depth:
                existingChild._avmDepthID = depth;
                existingChild._sessionID = vsItem.sessionID;
                this.setDepthToChild(depth, existingChild);
                this.adaptee._sessionID_childs[vsItem.sessionID] = existingChild;
                newChildren[i_2] = existingChild;
                //console.log("vsItem.exists", vsItem);
                if (!jump_forward) {
                    if (newChildren[i_2]._adapter) {
                        if (!newChildren[i_2].adapter.isColorTransformByScript()) {
                            newChildren[i_2].transform.clearColorTransform();
                        }
                        if (!newChildren[i_2].adapter.isBlockedByScript()
                            && !newChildren[i_2].noTimelineUpdate) {
                            newChildren[i_2].transform.clearMatrix3D();
                            newChildren[i_2].updateTimelineMask(null);
                        }
                        if (!newChildren[i_2].adapter.isVisibilityByScript()) {
                            newChildren[i_2].visible = true;
                        }
                    }
                    else {
                        newChildren[i_2].transform.clearColorTransform();
                        newChildren[i_2].transform.clearMatrix3D();
                        newChildren[i_2].visible = true;
                        newChildren[i_2].updateTimelineMask(null);
                    }
                }
            }
            else {
                var newChild = timeline.getChildInstance(vsItem.symbolID, vsItem.sessionID);
                if (this.adaptee.isSlice9ScaledMC && newChild.assetType == '[asset Sprite]') {
                    newChild.isSlice9ScaledSprite = true;
                }
                var depth = newChild._avmDepthID = vsItem.depth;
                newChild._sessionID = vsItem.sessionID;
                //this._depthToChilds[depth] = newChild;
                this.setDepthToChild(depth, newChild);
                this.adaptee._sessionID_childs[vsItem.sessionID] = newChild;
                newChildren[i_2] = newChild;
                if (vsItem.addedOnTargetFrame) {
                    newChildsOnTargetFrame[newChildsOnTargetFrame.length] = newChild;
                }
                else {
                    newChilds[newChilds.length] = newChild;
                }
            }
        }
        for (var i_3 = this.adaptee.numChildren - 1; i_3 >= 0; i_3--)
            if (newChildren.indexOf(this.adaptee.getChildAt(i_3)) < 0)
                this.adaptee.removeChildAt(i_3);
        for (var i_4 = 0; i_4 < newChildren.length; i_4++)
            if (!this.adaptee.contains(newChildren[i_4]))
                this.adaptee.addChildAt(newChildren[i_4], i_4);
        this.adaptee.preventScript = true;
        this.finalizeChildren(newChilds);
        this.adaptee.preventScript = false;
        // if there is a framescript on this frame, we queue it now, so it sits after the initAdapter of the children
        if (queue_script)
            this.queueFrameScripts(timeline, frame_idx, !queue_pass2);
        this.finalizeChildren(newChildsOnTargetFrame);
    };
    AVM1MovieClip.prototype.finalizeChildren = function (children) {
        var len = children.length;
        for (var i = 0; i < len; i++) {
            var newChild = children[i];
            if (newChild.adapter != newChild && newChild.adapter.deleteOwnProperties) {
                newChild.adapter.deleteOwnProperties();
            }
            newChild.reset();
            if (newChild.adapter != newChild) {
                // initAdapter is only used for avm1 to queue constructors / init-actions
                // for avm2 this is handled via FrameScriptManager.execute_as3_constructors_recursiv
                newChild.adapter.initAdapter();
            }
        }
    };
    AVM1MovieClip.prototype.removeMovieClip = function () {
        if (this.adaptee.isAVMScene) {
            return; // let's not remove root symbol
        }
        if (!this.adaptee.parent || away2avmDepth(this.adaptee._avmDepthID) < 0) {
            return;
        }
        var avm1parent = this.adaptee.parent.adapter;
        avm1parent.removeChildAtDepth(this.adaptee._avmDepthID);
        if (this.scriptMaskee) {
            this.scriptMaskee.setMask(null);
        }
        // to be sure
        this.freeFromScript();
        // todo should we unlink all child and call removeMovieClip recursive  or not?
        // workground for https://github.com/awaystudios/nitrome-conversions-batch7/issues/9
        this._childrenByName = {};
        this._depthToChilds = {};
        // going to ghost mode
        this.makeGhost();
        // todo why whe should keep adaptee?
        // this.adaptee?.dispose();
    };
    AVM1MovieClip.prototype._updateMouseEnabled = function (event, enabled) {
        if (!this.adaptee.isAVMScene) {
            if (event.isMouse) {
                if (enabled) {
                    this._mouseListenerCount++;
                    this.adaptee.mouseEnabled = true;
                    this.adaptee.mouseChildren = false;
                    if (event.isButton) {
                        this._mouseButtonListenerCount++;
                        this.adaptee.buttonMode = true;
                    }
                }
                else {
                    this._mouseListenerCount--;
                    if (this._mouseListenerCount <= 0) {
                        this._mouseListenerCount = 0;
                        this.adaptee.mouseEnabled = false;
                        this.adaptee.mouseChildren = true;
                    }
                    if (event.isButton) {
                        this._mouseButtonListenerCount--;
                        if (this._mouseButtonListenerCount <= 0) {
                            this._mouseButtonListenerCount = 0;
                            this.adaptee.buttonMode = false;
                        }
                    }
                }
            }
        }
        if (!this.enabled) {
            this.adaptee.mouseChildren = false;
        }
    };
    AVM1MovieClip.prototype.clone = function () {
        var clone = getAVM1Object(this.adaptee.clone(), this._avm1Context);
        //console.log("this.adaptee._symbol", this.adaptee.name)
        if (this.adaptee.name == 'piratefont') {
            var timeline = clone.adaptee.timeline;
            var targetTimeline = timeline;
            targetTimeline.frame_command_indices = [timeline.frame_command_indices[0]];
            targetTimeline.frame_recipe = [timeline.frame_recipe[0]];
            targetTimeline.keyframe_constructframes = [timeline.keyframe_constructframes[0]];
            targetTimeline.keyframe_durations = [timeline.keyframe_durations[0]];
            targetTimeline.keyframe_firstframes = [timeline.keyframe_firstframes[0]];
            targetTimeline.keyframe_indices = [timeline.keyframe_indices[0]];
        }
        return clone;
    };
    AVM1MovieClip.prototype.attachCustomConstructor = function () {
        if (this.adaptee) {
            var symbolClass_1 = this.adaptee.avm1Symbol;
            if (symbolClass_1) {
                this.alPut('__proto__', symbolClass_1._ownProperties.prototype.value);
                var myThis_1 = this;
                this.executeConstructor = function () {
                    symbolClass_1.alCall(myThis_1);
                    myThis_1.updateAllEvents();
                };
            }
        }
    };
    AVM1MovieClip.prototype.executeScript = function (actionsBlocks) {
        AVM1MovieClip.currentMCAssetNameSpace = this.adaptee.assetNamespace;
        AVM1TextField.syncQueedTextfields();
        if (!actionsBlocks) {
            var name_1 = this.adaptee.name.replace(/[^\w]/g, '');
            window.alert('actionsBlocks is empty, can not execute framescript' + name_1 + this.adaptee.currentFrameIndex);
            return;
        }
        if (!this.adaptee.parent)
            return;
        for (var k = 0; k < actionsBlocks.length; k++) {
            var actionsBlock = actionsBlocks[k];
            var script = function (actionsData) {
                this._avm1Context.executeActions(actionsData, this);
            }.bind(this, actionsBlock.data);
            script.precedence = actionsBlock.precedence;
            script.context = this.adaptee;
            script.call(this.adaptee);
        }
    };
    AVM1MovieClip.prototype.stopAllSounds = function () {
        var allProps = this.alGetKeys();
        for (var i = 0; i < allProps.length; i++) {
            var desc = this.alGetProperty(allProps[i]);
            var val = desc ? desc.value : null;
            if (val &&
                val._sound &&
                val._sound.isAsset &&
                val._sound.isAsset(WaveAudio)) {
                val.stop();
            }
        }
        var child;
        for (var i = this.adaptee.numChildren - 1; i >= 0; i--) {
            child = this.adaptee.getChildAt(i);
            if (child.isAsset(MovieClip) && child.adapter !== child) {
                child.adapter.freeFromScript();
            }
        }
    };
    // called from adaptee whenever the mc gets reset
    AVM1MovieClip.prototype.freeFromScript = function () {
        if (this._locked) {
            return;
        }
        //this.stopAllSounds();
        this.hasSwappedDepth = false;
        _super.prototype.freeFromScript.call(this);
        this._mouseButtonListenerCount = 0;
    };
    // called from adaptee whenever the mc gets added to a parent
    AVM1MovieClip.prototype.initAdapter = function () {
        this._dropTarget = '';
        this._mouseButtonListenerCount = 0;
        AVM1MovieClip.currentMCAssetNameSpace = this.adaptee.assetNamespace;
        // execute the init-actionscript that is stored on timeline
        for (var key in this.adaptee.timeline.avm1InitActions)
            this.executeScript(AVM1Globals._scenegraphFactory.createFrameScripts(this.adaptee.timeline.avm1InitActions[key], 0, 'initActionsData' + key, this.adaptee.id));
        if (this.initEvents) {
            initializeAVM1Object(this.adaptee, this._avm1Context, this.initEvents);
        }
        this.attachCustomConstructor();
        this.initialDepth = this.adaptee._avmDepthID;
        if (this.adaptee.onLoaded
            || this.adaptee.onConstruct
            || this.adaptee.onInitialize
            || this.executeConstructor) {
            FrameScriptManager.add_loaded_action_to_queue(this.adaptee);
        }
    };
    AVM1MovieClip.prototype.registerScriptObject = function (child, fromTimeline) {
        //	whenever multiple childs get registered for the same name, the child with the lowest depth wins
        //	this is true for objects added via
        //	timeline, attachMovie, duplicateMovieClip and CreateEmptyMovieClip and also when renaming objects
        if (fromTimeline === void 0) { fromTimeline = true; }
        //	if a avm1 variable for a registered child-name was defined via script,
        //	it always wins over the child registration.
        //	for example, after setting "testMC='something'" in script, trying to get "testMC" will return "something",
        //	and childs with the name "testMC" will no longer be accessible.
        //	this is true for all datatypes. once a variable is set by script, the value set by script will be returned.
        //	also true if the we do something like "testMC=null"
        //	if a avm1 script creates a variable as reference to a registered child, it behaves like this:
        //	the var is not updated if the child registered for the name changes
        //	if we do something like this:
        //			indirectRef = testMC;
        //			this.attachMovie("somethingFromLib", "testMC", -16380)
        //			testMC._x=100;
        //			testMC._y=100;
        //			indirectRef._x=100;
        //			indirectRef._y=100;
        //	this code will move both objects,
        //	because the attachMovie changes the object that is available under the name "testMC",
        //	but the indirectRef still holds the reference to the original object.
        //	however, if we remove the original child via removeMovieCLip or if it is removed via timeline,
        //	the variable will return a reference to the current object that might now be registered
        //	under the same name as the object that the variable was created for.
        // 	if a movieclip was tinted by a color object,
        //	and than another mc gets registered for the name, the tinting is applied to the new mc
        if (child.adapter != child)
            child.adapter.setEnabled(true);
        var name = child.name;
        if (name) {
            // 	in AVM1 FP<8, all names ignore case, so we can just convert everything to lower case
            //	for FP>=8, only method names can be handled this way, object names need to keep their case
            if (this.context.swfVersion < 8)
                name = name.toLowerCase();
            var hasVar = this.alHasOwnProperty(name);
            if (fromTimeline && hasVar) {
                // there exists a avm1 var for this name. Object registration should fail
                var ownDesc = this.alGetOwnProperty(name);
                if (ownDesc.isTextVar) {
                    //var registered for textfields-variables are a special case. they do not block registration
                    // instead we update the var to the new object
                    this.alPut(name, getAVM1Object(child, this.context));
                }
                else {
                    return;
                }
            }
            //	only register object if:
            //			- no object is registered for this name,
            //			- a already registered object has no valid parent
            //			- the already registered object has a higher depthID than the new object
            if (!this._childrenByName[name]
                || (this._childrenByName[name].adaptee && this._childrenByName[name].adaptee.parent == null)
                || (this._childrenByName[name].adaptee
                    && this._childrenByName[name].adaptee._avmDepthID > child._avmDepthID)) {
                //register new object
                this._childrenByName[name] = getAVM1Object(child, this.context);
                // timeline nodes should presented as vars too
                // but in readonly mode
                // you can't delete it by `delete` operation
                // and rewrite
                if (!fromTimeline) {
                    this.alPut(name, this._childrenByName[name]);
                }
            }
        }
    };
    AVM1MovieClip.prototype.unregisterScriptObject = function (child) {
        if (child.adapter._locked) {
            return;
        }
        //if (child && child.adapter != child)
        //	(<any>child.adapter).alPut("onEnterFrame", null);
        var name = child.name;
        if (name) {
            // 	in AVM1 FP<8, all names ignore case, so we can just convert everything to lower case
            //	for FP>=8, only method names can be handled this way, object names need to keep their case
            if (this.context.swfVersion < 8)
                name = name.toLowerCase();
            // unregister al-property, only if it is of type AVM1Symbolbase
            // this makes sure that objects from timeline unregister,
            // but other property-types are left alone
            var ownDesc = this.alGetOwnProperty(name);
            if (ownDesc && (ownDesc.value instanceof AVM1SymbolBase)) {
                this.alDeleteOwnProperty(name);
            }
            if (this._childrenByName[name] && this._childrenByName[name].adaptee.id == child.id) {
                /*if (this._childrenByName[name] && this._childrenByName[name].avmColor) {
                    this.unregisteredColors[name] = this._childrenByName[name].avmColor;
                }*/
                // 	check if there is another child with the same name on the movieclip
                //	and if so, register it instead
                //	if multiple childs match the name, get the one with highest depth
                //	attention: at this point the child that we unregister right now is still child of the mc
                var tmpChild = null;
                var newChild = null;
                for (var i = 0; i < this.adaptee.numChildren; i++) {
                    tmpChild = this.adaptee.getChildAt(i);
                    if (tmpChild != child && tmpChild.name && tmpChild.name.toLowerCase() == name) {
                        if (!newChild || newChild._avmDepthID > tmpChild._avmDepthID) {
                            newChild = tmpChild;
                        }
                    }
                }
                //	if we have a new child to register, we register it
                //	if not, we delete the registration for this name
                if (newChild) {
                    this._childrenByName[name] = getAVM1Object(newChild, this.context);
                    //if (this.unregisteredColors[name]) {
                    //	this.unregisteredColors[name].changeTarget(newChild.adapter);
                    //	this.unregisteredColors[name] = null;
                    //}
                }
                else {
                    delete this._childrenByName[name];
                }
            }
        }
    };
    AVM1MovieClip.prototype.getLatestObjectForName = function (name) {
        var hasVar = this.alHasOwnProperty(name);
        if (hasVar) {
            // there exists a avm1 var for this name. Object registration should fail
            return;
        }
        var tmpChild;
        var newChild;
        for (var i = 0; i < this.adaptee.numChildren; i++) {
            tmpChild = this.adaptee.getChildAt(i);
            if (tmpChild.name == name) {
                if (!newChild || newChild._avmDepthID > tmpChild._avmDepthID) {
                    newChild = tmpChild;
                }
            }
        }
        //	if we have a new child to register, we register it
        //	if not, we delete the registration for this name
        if (newChild) {
            if (this.context.swfVersion < 8)
                name = name.toLowerCase();
            this._childrenByName[name] = getAVM1Object(newChild, this.context);
            if (this.unregisteredColors[name]) {
                this.unregisteredColors[name].changeTarget(newChild.adapter);
                this.unregisteredColors[name] = null;
            }
        }
    };
    Object.defineProperty(AVM1MovieClip.prototype, "graphics", {
        get: function () {
            return this.adaptee.graphics;
        },
        enumerable: false,
        configurable: true
    });
    AVM1MovieClip.prototype.initAVM1SymbolInstance = function (context, awayObject) {
        var _this = this;
        this._childrenByName = Object.create(null);
        this._depthToChilds = Object.create(null);
        _super.prototype.initAVM1SymbolInstance.call(this, context, awayObject);
        this.dragListenerDelegate = function (event) { return _this.dragListener(event); };
        this.stopDragDelegate = function (event) { return _this.stopDrag(event); };
        this.dynamicallyCreated = false;
        this.adaptee = awayObject;
        this._initEventsHandlers();
    };
    AVM1MovieClip.prototype._lookupChildByName = function (name) {
        release || assert(alIsName(this.context, name));
        return this._childrenByName[name];
    };
    AVM1MovieClip.prototype._lookupChildInAS3Object = function (name) {
        //80pro todo lookupOptions
        /*let lookupOptions = LookupChildOptions.INCLUDE_NON_INITIALIZED;
        if (!this.context.isPropertyCaseSensitive) {
            lookupOptions |= LookupChildOptions.IGNORE_CASE;
        }*/
        var as3Child = this.adaptee.getChildByName(name); //, lookupOptions);
        return getAVM1Object(as3Child, this.context);
    };
    Object.defineProperty(AVM1MovieClip.prototype, "__targetPath", {
        get: function () {
            //return "";
            var target = this.get_target();
            var as3Root = this.adaptee.root;
            release || Debug.assert(as3Root);
            var level = this.context.globals._getLevelForRoot(as3Root);
            release || Debug.assert(level >= 0);
            var prefix = '_level' + level;
            return target != '/' ? prefix + target.replace(/\//g, '.') : prefix;
        },
        enumerable: false,
        configurable: true
    });
    AVM1MovieClip.prototype.getAwayJSID = function () {
        return this.adaptee.id;
    };
    AVM1MovieClip.prototype.attachAudio = function (id) {
        if (isNullOrUndefined(id)) {
            return; // ignoring all undefined objects, probably nothing to attach
        }
        if (id === false) {
            return; // TODO stop playing all attached audio source (when implemented).
        }
        // TODO implement NetStream and Microphone objects to make this work.
        console.warn('[AVM1MovieClip] attachAudio not implemented');
    };
    AVM1MovieClip.prototype.attachBitmap = function (bmp, depth, pixelSnapping, smoothing) {
        if (pixelSnapping === void 0) { pixelSnapping = 'auto'; }
        if (smoothing === void 0) { smoothing = false; }
        if (!bmp) {
            console.error('[AVM1MovieClip:attachBitmap] Bitmap not resolved! Skip attach for prevent crash', this.adaptee.id);
            return;
        }
        pixelSnapping = alCoerceString(this.context, pixelSnapping);
        smoothing = alToBoolean(this.context, smoothing);
        var awayBitmapImage2D = bmp.as3BitmapData;
        awayBitmapImage2D.transparent = true;
        var billboard = bmp.getBillboard(pixelSnapping, smoothing);
        // bitmap not handle events
        billboard.mouseEnabled = false;
        this.addChildAtDepth(billboard, depth);
    };
    AVM1MovieClip.prototype._constructMovieClipSymbol = function (symbolId, name) {
        symbolId = alToString(this.context, symbolId);
        name = alToString(this.context, name);
        var symbol = AssetLibrary.getAsset(symbolId, this.adaptee.assetNamespace);
        if (!symbol) {
            return undefined;
        }
        var mc = symbol.adaptee.clone();
        mc.name = name;
        getAVM1Object(mc, this._avm1Context);
        return mc;
    };
    AVM1MovieClip.prototype.get$version = function () {
        return '';
        //return Capabilities.version;
    };
    AVM1MovieClip.prototype.rgbaToArgb = function (float32Color) {
        var r = (float32Color & 0xff000000) >>> 24;
        var g = (float32Color & 0xff0000) >>> 16;
        var b = (float32Color & 0xff00) >>> 8;
        var a = float32Color & 0xff;
        return (a << 24) | (r << 16) | (g << 8) | b;
    };
    AVM1MovieClip.prototype.attachMovie = function (symbolId, name, depth, initObject) {
        if (!this._constructMovieClipSymbol)
            return;
        if (name && alIsArray(this.context, name)) {
            name = name.value[0];
        }
        var mc = this._constructMovieClipSymbol(symbolId, name);
        if (!mc) {
            return undefined;
        }
        if (!mc.name) {
            mc.name = '';
        }
        depth = alToNumber(this.context, depth);
        return this.initChildMovie(mc, name, depth, initObject, true);
    };
    AVM1MovieClip.prototype.initChildMovie = function (mc, name, depth, initObject, fromTimeline) {
        if (fromTimeline === void 0) { fromTimeline = false; }
        var oldAVMMC = this._childrenByName[name.toLowerCase()];
        mc.reset();
        mc.name = name;
        var avmMc = this.addChildAtDepth(mc, avm2AwayDepth(depth));
        initObject && avmMc._init(initObject);
        if (oldAVMMC && oldAVMMC.avmColor) {
            oldAVMMC.avmColor.changeTarget(avmMc);
        }
        if (mc.timeline && mc.timeline.isButton) {
            mc.addButtonListeners();
        }
        avmMc.dynamicallyCreated = true;
        name && this.registerScriptObject(mc, fromTimeline);
        var cCtor = avmMc.executeConstructor;
        avmMc.executeConstructor = null;
        cCtor && cCtor();
        return avmMc;
    };
    AVM1MovieClip.prototype.beginFill = function (color, alpha) {
        color = alToInt32(this.context, color);
        if (typeof alpha == 'undefined') {
            if (arguments.length == 2) {
                // alpha was set with "undefined" variable - should be 0
                alpha = 0;
            }
            else if (arguments.length <= 1) {
                // alpha was not set at all - should be 100
                alpha = 100;
            }
        }
        alpha = alToNumber(this.context, alpha);
        this.graphics.beginFill(color, alpha / 100.0);
    };
    AVM1MovieClip.prototype.beginBitmapFill = function (bmp, matrix, repeat, smoothing) {
        if (matrix === void 0) { matrix = null; }
        if (repeat === void 0) { repeat = true; }
        if (smoothing === void 0) { smoothing = false; }
        // nullable cast, return null if can't convert to as3 bitmap
        var bmpNative = toAS3BitmapData(bmp);
        if (!bmpNative) {
            return;
        }
        var matrixNative = isNullOrUndefined(matrix) ? null : toAS3Matrix(matrix);
        repeat = alToBoolean(this.context, repeat);
        smoothing = alToBoolean(this.context, smoothing);
        //console.warn('[AVM1MovieClip] beginBitmapFill not implemented');
        this.graphics.beginBitmapFill(bmpNative, matrixNative, repeat, smoothing);
    };
    AVM1MovieClip.prototype.beginGradientFill = function (fillType, colors, alphas, ratios, matrix, spreadMethod, interpolationMethod, focalPointRatio) {
        if (spreadMethod === void 0) { spreadMethod = 'pad'; }
        if (interpolationMethod === void 0) { interpolationMethod = 'rgb'; }
        if (focalPointRatio === void 0) { focalPointRatio = 0.0; }
        var context = this.context;
        // fillType = alToString(this.context, fillType);
        var colorsNative = AVM1ArrayNative.mapToJSArray(colors, function (item) { return alToInt32(context, item); });
        var alphasNative = AVM1ArrayNative.mapToJSArray(alphas, function (item) { return alToNumber(context, item) / 100.0; });
        var ratiosNative = AVM1ArrayNative.mapToJSArray(ratios, function (item) { return alToNumber(context, item); });
        var matrixNative = null;
        if (isNullOrUndefined(matrix)) {
            console.warn('[AVM1MovieClip] beginGradientFill not fully implemented');
        }
        spreadMethod = alToString(context, spreadMethod);
        interpolationMethod = alToString(context, interpolationMethod);
        focalPointRatio = alToNumber(context, focalPointRatio);
        this.graphics.beginGradientFill(fillType, colorsNative, alphasNative, ratiosNative, matrixNative, spreadMethod, interpolationMethod, focalPointRatio);
    };
    AVM1MovieClip.prototype._callFrame = function (frame) {
        var scripts;
        if (typeof frame === 'string') {
            frame = frame.toLowerCase();
            scripts = this.adaptee.timeline.getScriptForLabel(this.adaptee, frame, true);
        }
        else if (typeof frame === 'number') {
            scripts = this.adaptee.timeline.get_script_for_frame(this.adaptee, frame - 1, true);
        }
        if (scripts)
            this.executeScript(scripts);
    };
    AVM1MovieClip.prototype.clear = function () {
        this.graphics.clear();
    };
    AVM1MovieClip.prototype._updateChildName = function (child, oldName, newName) {
        if (oldName === newName) {
            return;
        }
        oldName && this._removeChildName(child, oldName);
        newName && this._addChildName(child, newName);
    };
    AVM1MovieClip.prototype._removeChildName = function (child, name) {
        release || assert(name);
        if (!this.context.isPropertyCaseSensitive) {
            name = name.toLowerCase();
        }
        if (!this._childrenByName || !this._childrenByName[name])
            return;
        if (this._childrenByName[name] !== child) {
            return;
        }
        var newChildForName = this._lookupChildInAS3Object(name);
        if (newChildForName) {
            this._childrenByName[name] = newChildForName;
        }
        else {
            delete this._childrenByName[name];
        }
    };
    AVM1MovieClip.prototype._addChildName = function (child, name) {
        release || assert(name);
        if (!this.context.isPropertyCaseSensitive) {
            name = name.toLowerCase();
        }
        release || assert(this._childrenByName[name] !== child);
        var currentChild = this._childrenByName[name];
        if (!currentChild || currentChild.getDepth() > child.getDepth()) {
            this._childrenByName[name] = child;
        }
    };
    AVM1MovieClip.prototype.createEmptyMovieClip = function (name, depth) {
        //	creates a new empty movieclip.
        //	if a mc already exists for the name,
        //	but at different depth, it will create a new movieclip, and also keep the existing alive
        //	if a mc already exists at same depth, it will replace the existing movieclip with the new one
        name = alToString(this.context, name);
        var mc = AVM1Globals._scenegraphFactory.createMovieClip();
        mc.name = name;
        mc.assetNamespace = this.adaptee.assetNamespace;
        //console.log("createEmptyMovieClip", name, avm2AwayDepth(depth));
        var avmMC = this.addChildAtDepth(mc, avm2AwayDepth(depth));
        avmMC.dynamicallyCreated = true;
        this.registerScriptObject(mc, false);
        // set mouseEnabled to false. if any listener is applied, this will than be set back to true
        mc.mouseEnabled = false;
        //mc.mouseChildren=false;
        // dynamicallyCreated needs to be set after adding child, otherwise it gets reset
        return avmMC;
    };
    AVM1MovieClip.prototype.createTextField = function (name, depth, x, y, width, height) {
        name = alToString(this.context, name);
        var text = new TextField();
        text.name = name;
        getAVM1Object(text, this._avm1Context);
        var myTF = this.addChildAtDepth(text, avm2AwayDepth(depth));
        this.registerScriptObject(text, false);
        text.x = x;
        text.y = y;
        text.width = width;
        text.height = height;
        myTF.dynamicallyCreated = true;
        return myTF;
    };
    AVM1MovieClip.prototype.get_currentframe = function () {
        return this.adaptee.currentFrameIndex + 1;
    };
    AVM1MovieClip.prototype.curveTo = function (controlX, controlY, anchorX, anchorY) {
        controlX = alToNumber(this.context, controlX);
        controlY = alToNumber(this.context, controlY);
        anchorX = alToNumber(this.context, anchorX);
        anchorY = alToNumber(this.context, anchorY);
        this.graphics.curveTo(controlX, controlY, anchorX, anchorY);
    };
    AVM1MovieClip.prototype.setDropTarget = function (dropTarget) {
        if (dropTarget) {
            //console.log((<AVMRaycastPicker>AVM1Stage.stage.view.mousePicker).getDropTarget().name);
            var names = [];
            while (dropTarget) {
                if (dropTarget.isAVMScene) {
                    dropTarget = null;
                }
                else {
                    if (dropTarget.name != null)
                        names.push(dropTarget.name);
                    dropTarget = dropTarget.parent;
                }
            }
            var i = names.length;
            var mc_path = '';
            while (i > 0) {
                i--;
                if (names[i] != '')
                    mc_path += '/';
                mc_path += names[i];
            }
            //console.log(mc_path);
            this._dropTarget = mc_path;
            return;
        }
        this._dropTarget = '';
    };
    AVM1MovieClip.prototype.get_droptarget = function () {
        return this._dropTarget;
    };
    AVM1MovieClip.prototype.duplicateMovieClip = function (name, depth, initObject) {
        name = alToString(this.context, name);
        if (name == this.adaptee.name) {
            return this;
        }
        var parent = this.get_parent();
        if (!parent) {
            console.warn('[AVM1MovieClip] duplicateMovieClip could not get parent');
            parent = this.context.resolveTarget(null);
        }
        var mc = this.clone().adaptee;
        var avmMc = parent.initChildMovie(mc, name, depth, null, false);
        avmMc.placeObjectTag = this.placeObjectTag;
        avmMc.initEvents = this.initEvents;
        var new_matrix = mc.transform.matrix3D;
        var originalMatrix = this.adaptee.transform.matrix3D._rawData;
        new_matrix._rawData[0] = originalMatrix[0];
        new_matrix._rawData[1] = originalMatrix[1];
        new_matrix._rawData[4] = originalMatrix[4];
        new_matrix._rawData[5] = originalMatrix[5];
        new_matrix._rawData[12] = originalMatrix[12];
        new_matrix._rawData[13] = originalMatrix[13];
        mc.transform.invalidateComponents();
        mc.alpha = this.adaptee.alpha;
        mc.blendMode = this.adaptee.blendMode;
        mc.cacheAsBitmap = this.adaptee.cacheAsBitmap;
        //filters, clone array if it exist
        var f = this.getFilters();
        f && avmMc.setFilters(new AVM1ArrayNative(this.context, f.value.slice()));
        // we should apply init after copy transform
        if (initObject) {
            avmMc._init(initObject);
        }
        return avmMc;
    };
    AVM1MovieClip.prototype.endFill = function () {
        this.graphics.endFill();
    };
    AVM1MovieClip.prototype.getForceSmoothing = function () {
        console.warn('[AVM1MovieClip] getForceSmoothing');
        return false;
    };
    AVM1MovieClip.prototype.setForceSmoothing = function (value) {
        value = alToBoolean(this.context, value);
        console.warn('[AVM1MovieClip] setForceSmoothing');
    };
    AVM1MovieClip.prototype.get_framesloaded = function () {
        // Unimplemented: this.adaptee.framesLoaded
        console.warn('[AVM1MovieClip] get_framesloaded');
        return this.adaptee.numFrames;
    };
    AVM1MovieClip.prototype.getBounds = function (bounds) {
        if (!bounds)
            return undefined;
        var box = this._stage.pickGroup
            .getBoundsPicker(this.node)
            .getBoxBounds(bounds.node, true, true);
        if (!box) {
            console.warn('[AVM1 MovieClip::getBounds] Bounds is null!', this.adaptee.id);
            return undefined;
        }
        return convertAS3RectangeToBounds(box, this.context);
    };
    AVM1MovieClip.prototype.getBytesLoaded = function () {
        //var loaderInfo = this.adaptee.loaderInfo;
        return this.adaptee.currentFrameIndex >= 0 ? 100 : -1; //loaderInfo.bytesLoaded;
    };
    AVM1MovieClip.prototype.getBytesTotal = function () {
        //var loaderInfo = this.adaptee.loaderInfo;
        return 100; //loaderInfo.bytesTotal;
    };
    AVM1MovieClip.prototype.getInstanceAtDepth = function (depth) {
        var child = this.getDepthToChild(avm2AwayDepth(depth));
        if (!child) {
            return null;
        }
        return getAVM1Object(child, this.context);
    };
    AVM1MovieClip.prototype.getNextHighestDepth = function () {
        if (this.context.swfVersion < 7)
            return 0;
        return away2avmDepth(this._nextHighestDepth);
    };
    AVM1MovieClip.prototype.getRect = function (bounds) {
        if (!bounds || !bounds.node)
            return undefined;
        return convertAS3RectangeToBounds(this._stage.pickGroup.getBoundsPicker(this.node).getBoxBounds(bounds.node, false, true), this.context);
    };
    AVM1MovieClip.prototype.getSWFVersion = function () {
        return this.context.swfVersion;
    };
    AVM1MovieClip.prototype.getTextSnapshot = function () {
        console.warn('[AVM1MovieClip] getTextSnapshot');
    };
    AVM1MovieClip.prototype.getURL = function (url, target, _method) {
        url = String(url);
        if (url.toLowerCase().indexOf('fscommand:') === 0) {
            console.warn('[AVM1MovieClip] fsCommand not implemented ');
            return;
        }
        window.open(url, target);
    };
    AVM1MovieClip.prototype.globalToLocal = function (pt) {
        if (!pt)
            return;
        var tmp = toAS3Point(pt);
        this.node.globalToLocal(tmp, tmp);
        copyAS3PointTo(tmp, pt);
    };
    AVM1MovieClip.prototype.gotoAndPlay = function (frame) {
        if (this.protoTypeChanged || frame == null)
            return;
        if (Array.isArray(frame)) {
            if (frame.length == 0)
                return;
            frame = frame[0];
        }
        if (frame instanceof AVM1ArrayNative) {
            if (!frame.value || frame.value.length == 0)
                return;
            frame = frame.value[0];
        }
        if (typeof frame === 'string') {
            var labelName = frame.toLowerCase();
            if (this.adaptee.timeline._labels[labelName] == null) {
                frame = parseInt(frame);
                if (!isNaN(frame)) {
                    this.adaptee.currentFrameIndex = frame - 1;
                    this.adaptee.play();
                }
                return;
            }
        }
        if (typeof frame === 'number' && frame <= 0)
            return;
        this.adaptee.play();
        this._gotoFrame(frame);
    };
    AVM1MovieClip.prototype.gotoAndStop = function (frame) {
        if (this.protoTypeChanged || frame == null)
            return;
        if (Array.isArray(frame)) {
            if (frame.length == 0)
                return;
            frame = frame[0];
        }
        if (frame instanceof AVM1ArrayNative) {
            if (!frame.value || frame.value.length == 0)
                return;
            frame = frame.value[0];
        }
        if (typeof frame === 'number' && frame <= 0)
            return;
        this.adaptee.stop();
        this._gotoFrame(frame);
    };
    AVM1MovieClip.prototype._gotoFrame = function (frame) {
        if (typeof frame === 'number') {
            if (frame % 1 !== 0) {
                frame = frame.toString();
            }
        }
        if (typeof frame === 'string') {
            var labelName = frame.toLowerCase();
            if (this.adaptee.timeline._labels[labelName] == null) {
                frame = parseInt(frame);
                if (!isNaN(frame)) {
                    this.adaptee.currentFrameIndex = frame - 1;
                }
                return;
            }
            this.adaptee.jumpToLabel(labelName);
        }
        else {
            this.adaptee.currentFrameIndex = frame - 1;
        }
    };
    AVM1MovieClip.prototype.getHitArea = function () {
        return this._hitArea;
    };
    AVM1MovieClip.prototype.setHitArea = function (value) {
        // The hitArea getter always returns exactly the value set here, so we have to store that.
        this._hitArea = value;
        var obj = value ? value.node : null;
        if (obj && !obj.container.isAsset(MovieClip))
            obj = null;
        // 	MA_GBR_0700AAx0100 is the first lesson encountered that makes use of hitArea
        // 	if the hitArea is set, the mouse-interactions on the ducks stop working
        // this.adaptee.hitArea=obj;
    };
    AVM1MovieClip.prototype.hitTest = function (x, y, shapeFlag) {
        if (arguments.length < 2) {
            var target = x;
            if (typeof target === 'string')
                target = this.context.resolveTarget(target);
            if (target == undefined || !target.node)
                return false; // target is undefined or not a AVM1 display object, returning false.
            return AVM1Stage.avmStage.pickGroup
                .getBoundsPicker(this.node)
                .hitTestObject(AVM1Stage.avmStage.pickGroup.getBoundsPicker(target.node));
        }
        x = alToNumber(this.context, x);
        y = alToNumber(this.context, y);
        var r = this.get_root();
        if (!r) {
            if (this._hitReportCount < 10) {
                console.warn('[AVM1MovieClip:: hitTest] Root return undef! Return false to prevent crash!', this.adaptee.id);
                this._hitReportCount++;
                if (this._hitReportCount === 10) {
                    console.warn('[AVM1MovieClip:: hitTest] To many reports, supress other!');
                }
            }
            return false;
        }
        x += r.get_x();
        y += r.get_y();
        shapeFlag = alToBoolean(this.context, shapeFlag);
        return this._stage.pickGroup.getBoundsPicker(this.node).hitTestPoint(x, y, shapeFlag);
    };
    AVM1MovieClip.prototype.lineGradientStyle = function (fillType, colors, alphas, ratios, matrix, spreadMethod, interpolationMethod, focalPointRatio) {
        if (spreadMethod === void 0) { spreadMethod = 'pad'; }
        if (interpolationMethod === void 0) { interpolationMethod = 'rgb'; }
        if (focalPointRatio === void 0) { focalPointRatio = 0.0; }
        var context = this.context;
        // fillType = alToString(this.context, fillType);
        var colorsNative = AVM1ArrayNative.mapToJSArray(colors, function (item) { return alToInt32(context, item); });
        var alphasNative = AVM1ArrayNative.mapToJSArray(alphas, function (item) { return alToNumber(context, item) / 100.0; });
        var ratiosNative = AVM1ArrayNative.mapToJSArray(ratios, function (item) { return alToNumber(context, item); });
        var matrixNative = null;
        if (isNullOrUndefined(matrix)) {
            somewhatImplemented('AVM1MovieClip.lineGradientStyle');
        }
        spreadMethod = alToString(context, spreadMethod);
        interpolationMethod = alToString(context, interpolationMethod);
        focalPointRatio = alToNumber(context, focalPointRatio);
        this.graphics.lineGradientStyle(fillType, colorsNative, alphasNative, ratiosNative, matrixNative, spreadMethod, interpolationMethod, focalPointRatio);
    };
    AVM1MovieClip.prototype.lineStyle = function (thickness, rgb, alpha, pixelHinting, noScale, capsStyle, jointStyle, miterLimit) {
        if (thickness === void 0) { thickness = NaN; }
        if (rgb === void 0) { rgb = 0x000000; }
        if (alpha === void 0) { alpha = 100; }
        if (pixelHinting === void 0) { pixelHinting = false; }
        if (noScale === void 0) { noScale = 'normal'; }
        if (capsStyle === void 0) { capsStyle = 'round'; }
        if (jointStyle === void 0) { jointStyle = 'round'; }
        if (miterLimit === void 0) { miterLimit = 3; }
        thickness = alToNumber(this.context, thickness);
        rgb = alToInt32(this.context, rgb);
        pixelHinting = alToBoolean(this.context, pixelHinting);
        noScale = alToString(this.context, noScale);
        var capsStyleInt = AVM1MovieClip.capStyleMapStringToInt[alToString(this.context, capsStyle)];
        var jointStyleInt = AVM1MovieClip.jointStyleMapStringToInt[alToString(this.context, jointStyle)];
        miterLimit = alToNumber(this.context, miterLimit);
        this.graphics.lineStyle(thickness, rgb, alpha / 100.0, pixelHinting, AVM1MovieClip.noScaleDictionary[noScale], capsStyleInt, jointStyleInt, miterLimit);
    };
    AVM1MovieClip.prototype.lineTo = function (x, y) {
        x = toTwipFloor(alToNumber(this.context, x));
        y = toTwipFloor(alToNumber(this.context, y));
        this.graphics.lineTo(x, y);
    };
    AVM1MovieClip.prototype.loadMovie = function (url, method) {
        var loaderHelper = new AVM1LoaderHelper(this.context);
        loaderHelper.loadMovieAt(url, method, this).then(function (mc) {
            if (!mc) {
                warning('loadMovie - content is null');
                return;
            }
            //this.adaptee.stop();
        });
    };
    AVM1MovieClip.prototype.loadVariables = function (url, method) {
        this.context.actions._loadVariables(this, url, method);
    };
    AVM1MovieClip.prototype.localToGlobal = function (pt) {
        if (!pt) {
            return;
        }
        var tmp = toAS3Point(pt);
        this.node.localToGlobal(tmp, tmp);
        copyAS3PointTo(tmp, pt);
    };
    AVM1MovieClip.prototype.get_lockroot = function () {
        return this._lockroot;
    };
    AVM1MovieClip.prototype.set_lockroot = function (value) {
        somewhatImplemented('AVM1MovieClip._lockroot');
        this._lockroot = alToBoolean(this.context, value);
    };
    AVM1MovieClip.prototype.moveTo = function (x, y) {
        x = toTwipFloor(alToNumber(this.context, x));
        y = toTwipFloor(alToNumber(this.context, y));
        this.graphics.moveTo(x, y);
    };
    AVM1MovieClip.prototype.nextFrame = function () {
        this.adaptee.stop();
        ++this.adaptee.currentFrameIndex;
    };
    AVM1MovieClip.prototype.nextScene = function () {
        console.warn('[AVM1MovieClip] nextScene not implemented');
    };
    AVM1MovieClip.prototype.play = function () {
        this.adaptee.play();
    };
    AVM1MovieClip.prototype.prevFrame = function () {
        this.adaptee.stop();
        --this.adaptee.currentFrameIndex;
    };
    AVM1MovieClip.prototype.prevScene = function () {
        console.warn('[AVM1MovieClip] prevScene not implemented');
    };
    AVM1MovieClip.prototype.setMask = function (mask) {
        if (mask == null) {
            // Cancel a mask.
            this.adaptee.scriptMask = null;
            return;
        }
        mask.scriptMaskee = this;
        mask = this.context.resolveTarget(mask);
        if (mask)
            this.adaptee.scriptMask = mask.adaptee;
    };
    AVM1MovieClip.prototype.startDrag = function (lock, left, top, right, bottom) {
        var _a, _b;
        var stage = this._stage;
        if (AVM1MovieClip.currentDraggedMC && AVM1MovieClip.currentDraggedMC != this) {
            AVM1MovieClip.currentDraggedMC.stopDrag();
        }
        AVM1MovieClip.currentDraggedMC = this;
        lock = alToBoolean(this.context, lock);
        this._dragBounds = null;
        if (left > right) {
            _a = [left, right], right = _a[0], left = _a[1];
        }
        if (top > bottom) {
            _b = [bottom, top], top = _b[0], bottom = _b[1];
        }
        if (arguments.length > 1) {
            left = alToNumber(this.context, left);
            top = alToNumber(this.context, top);
            right = alToNumber(this.context, right);
            bottom = alToNumber(this.context, bottom);
            //console.log("left", left,"top", top, "right", right, "bottom", bottom );
            this._dragBounds = new Rectangle(left, top, right - left, bottom - top);
        } //todo: listen on stage
        if (this.isDragging) {
            return;
        }
        this.isDragging = true;
        var parentNode = stage.view.getNode(this.adaptee.parent);
        var localX = stage.getLocalMouseX(parentNode);
        var localY = stage.getLocalMouseY(parentNode);
        this.startDragPoint = this.startDragPoint || new Point();
        this.startDragPoint.setTo(localX, localY);
        if (lock) {
            this.adaptee.x = this.startDragPoint.x;
            this.adaptee.y = this.startDragPoint.y;
        }
        if (this._dragBounds)
            this.checkBounds();
        this.startDragMCPosition.x = this.adaptee.x;
        this.startDragMCPosition.y = this.adaptee.y;
        var dragNode = this.node;
        stage.view.stage.addEventListener(AwayMouseEvent.MOUSE_MOVE, this.dragListenerDelegate);
        // PLZ, never set this! Because this damage a dragging when it started without event
        //stage.mousePicker.dragNode = dragNode
        var collision = stage.mousePicker.pickGroup.abstractions
            .getAbstraction(dragNode)
            .pickingCollision;
        // collision MUST has rootNode, otherwise will be drag bug
        collision.rootNode = dragNode;
        //console.log(collision, dragNode);
        stage.mouseManager.startDragObject(collision, true);
    };
    AVM1MovieClip.prototype.dragListener = function (_e) {
        if (!this.adaptee.parent) {
            return;
        }
        var parentNode = this._stage.view.getNode(this.adaptee.parent);
        var localX = this._stage.getLocalMouseX(parentNode);
        var localY = this._stage.getLocalMouseY(parentNode);
        this.adaptee.x = this.startDragMCPosition.x + (localX - this.startDragPoint.x);
        this.adaptee.y = this.startDragMCPosition.y + (localY - this.startDragPoint.y);
        if (this._dragBounds)
            this.checkBounds();
    };
    AVM1MovieClip.prototype.checkBounds = function () {
        if (this.adaptee.x < (this._dragBounds.left)) {
            this.adaptee.x = this._dragBounds.left;
        }
        if (this.adaptee.x > (this._dragBounds.right)) {
            this.adaptee.x = (this._dragBounds.right);
        }
        if (this.adaptee.y < this._dragBounds.top) {
            this.adaptee.y = this._dragBounds.top;
        }
        if (this.adaptee.y > (this._dragBounds.bottom)) {
            this.adaptee.y = this._dragBounds.bottom;
        }
    };
    AVM1MovieClip.prototype.stop = function () {
        return this.adaptee.stop();
    };
    AVM1MovieClip.prototype.stopDrag = function (_e) {
        if (_e === void 0) { _e = null; }
        if (AVM1MovieClip.currentDraggedMC && AVM1MovieClip.currentDraggedMC != this) {
            AVM1MovieClip.currentDraggedMC.stopDrag();
        }
        this.isDragging = false;
        AVM1MovieClip.currentDraggedMC = null;
        this._stage.mousePicker.dragNode = null;
        this._stage.mouseManager.stopDragObject();
        this._stage.view.stage.removeEventListener(AwayMouseEvent.MOUSE_MOVE, this.dragListenerDelegate);
    };
    /**
     * @param child1 first child that should be swapped
     * @param child2 second child that should be swapped (optionally)
     * @param depth1 new depth for first child
     * @param depth2 new depth for second child (optionally)
     */
    AVM1MovieClip.prototype.swapChildrenToDepth = function (child1, child2, depth1, depth2) {
        if (child2 && this._depthToChilds[depth1]) {
            // if no child2 was passed, check if there exists a child at depth1
            // depth1 is the target depth for child1, so if it is occupied we want to swap the two children
            // if its not occupied, we can just set the new depth for child1, order childs by depth and be done with it
            child2 = this.getDepthToChild(depth1).adapter;
        }
        delete this.adaptee._sessionID_childs[child1.adaptee._sessionID];
        child1.adaptee._avmDepthID = depth1;
        child1.adaptee._sessionID = -1;
        this.adaptee.addChildAt(child1.adaptee, this.getIndexFromDepthOrTop(depth1));
        child1.hasSwappedDepth = true;
        this.setDepthToChild(depth1, child1.adaptee);
        if (child2) {
            delete this.adaptee._sessionID_childs[child2.adaptee._sessionID];
            this.setDepthToChild(depth2, child2.adaptee);
            child2.adaptee._avmDepthID = depth2;
            child2.adaptee._sessionID = -1;
            this.adaptee.addChildAt(child2.adaptee, this.getIndexFromDepthOrTop(depth2));
            child2.hasSwappedDepth = true;
        }
        else {
            this.setDepthToChild(depth2);
        }
        // re-trap a events, new depth require update mapping for it
        child1 && child1.updateAllEvents();
        child2 && child2.updateAllEvents();
        if (this.adaptee.name && parent) {
            // we need to check if child registration must be updated
            this.getLatestObjectForName(child1.adaptee.name);
            if (child2) {
                this.getLatestObjectForName(child2.adaptee.name);
            }
        }
    };
    AVM1MovieClip.prototype.swapDepths = function (target) {
        // if this is the scene, or if no parent exists, we do not want to do anything
        if (this.adaptee.isAVMScene || !this.get_parent()) {
            return;
        }
        var parent = this.get_parent();
        var targetChild = null;
        if (!parent) {
            console.warn('[AVM1MovieClip] swapDepth called for object with no parent');
            return;
        }
        if (typeof target === 'undefined') {
            console.warn('[AVM1MovieClip] swapDepth called with undefined as target depth');
            return;
        }
        if (typeof target === 'number') {
            target = avm2AwayDepth(target);
            if (this.adaptee._avmDepthID == target)
                return;
            //targetChild = parent._depthToChilds[target];
            targetChild = parent.getDepthToChild(target);
            if (targetChild) {
                targetChild = targetChild.adapter;
            }
            //console.log("swap to number", this.adaptee.name, target);
        }
        else if (target.adaptee) {
            var targetParent = target.get_parent();
            if (targetParent != parent)
                return;
            targetChild = target;
            target = targetChild.adaptee._avmDepthID;
            //console.log("swap to children", this.adaptee.name, target.adaptee.name);
        }
        parent.swapChildrenToDepth(this, targetChild, target, this.adaptee._avmDepthID);
    };
    AVM1MovieClip.prototype.getTabChildren = function () {
        return getAwayObjectOrTemplate(this).tabChildren;
    };
    AVM1MovieClip.prototype.setTabChildren = function (value) {
        getAwayObjectOrTemplate(this).tabChildren = alToBoolean(this.context, value);
    };
    AVM1MovieClip.prototype.get_totalframes = function () {
        return this.adaptee.numFrames;
    };
    AVM1MovieClip.prototype.getTrackAsMenu = function () {
        console.warn('[AVM1MovieClip] getTrackAsMenu not implemented');
        return getAwayObjectOrTemplate(this).trackAsMenu;
    };
    AVM1MovieClip.prototype.setTrackAsMenu = function (value) {
        console.warn('[AVM1MovieClip] setTrackAsMenu not implemented');
        getAwayObjectOrTemplate(this).trackAsMenu = alToBoolean(this.context, value);
    };
    AVM1MovieClip.prototype.unloadMovie = function () {
        var nativeObject = this.adaptee;
        this.adaptee.constructedKeyFrameIndex = 0;
        this.adaptee.stop();
        nativeObject.removeChildren(0, nativeObject.numChildren);
        if ((this.dynamicallyCreated || this.hasSwappedDepth) && nativeObject.parent && nativeObject.parent.adapter) {
            nativeObject.parent.adapter.unregisterScriptObject(nativeObject);
        }
    };
    AVM1MovieClip.prototype.getUseHandCursor = function () {
        return this.adaptee.useHandCursor;
    };
    AVM1MovieClip.prototype.setUseHandCursor = function (value) {
        if (!this.adaptee)
            return;
        this.adaptee.useHandCursor = value;
    };
    AVM1MovieClip.prototype.setParameters = function (parameters) {
        for (var paramName in parameters) {
            if (!this.alHasProperty(paramName)) {
                this.alPut(paramName, parameters[paramName]);
            }
        }
    };
    // Special and children names properties resolutions
    AVM1MovieClip.prototype._resolveLevelNProperty = function (name) {
        release || assert(alIsName(this.context, name));
        if (name === '_level0' || name === '_level') {
            return this.context.resolveRoot();
        }
        else if (name === '_root') {
            return this.context.resolveRoot();
        }
        else if (name.indexOf('_level') === 0) {
            var level = name.substring(6);
            var levelNum = level | 0;
            if (levelNum > 0 && level == levelNum) {
                return this.context.resolveLevel(levelNum);
            }
        }
        return null;
    };
    AVM1MovieClip.prototype._getCachedPropertyResult = function (value) {
        if (!this._cachedPropertyResult) {
            this._cachedPropertyResult = {
                flags: 64 /* AVM1PropertyFlags.DATA */ | 1 /* AVM1PropertyFlags.DONT_ENUM */, value: value
            };
        }
        else {
            this._cachedPropertyResult.value = value;
        }
        return this._cachedPropertyResult;
    };
    AVM1MovieClip.prototype.alGetOwnProperty = function (name) {
        var desc = _super.prototype.alGetOwnProperty.call(this, name);
        if (desc) {
            return desc;
        }
        if (name[0] === '_') {
            if ((name[1] === 'l' && name.indexOf('_level') === 0 ||
                name[1] === 'r' && name.indexOf('_root') === 0)) {
                var level = this._resolveLevelNProperty(name);
                if (level) {
                    return this._getCachedPropertyResult(level);
                }
            }
            else if (name.toLowerCase() in MovieClipProperties) {
                // For MovieClip's properties that start from '_' case does not matter.
                return _super.prototype.alGetOwnProperty.call(this, name.toLowerCase());
            }
        }
        if (hasAwayJSAdaptee(this)) {
            var child = this._lookupChildByName(name);
            if (child) {
                return this._getCachedPropertyResult(child);
            }
        }
        return undefined;
    };
    AVM1MovieClip.prototype.alGetOwnPropertiesKeys = function () {
        var keys = _super.prototype.alGetOwnPropertiesKeys.call(this);
        // if it's a movie listing the children as well
        if (!hasAwayJSAdaptee(this)) {
            return keys; // not initialized yet
        }
        var processed = Object.assign(Object.create(null), this._childrenByName);
        for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
            var key = keys_1[_i];
            processed[key] = true;
        }
        return Object.keys(processed);
    };
    AVM1MovieClip.prototype._init = function (initObject) {
        var _this = this;
        if (initObject instanceof AVM1Object) {
            alForEachProperty(initObject, function (name) {
                _this.alPut(name, initObject.alGet(name));
            }, null);
        }
    };
    AVM1MovieClip.prototype._initEventsHandlers = function () {
        this.bindEvents(EventsListForMC);
    };
    AVM1MovieClip.currentMCAssetNameSpace = '';
    AVM1MovieClip.currentDraggedMC = null;
    // if a stop-action occurs, we check if a child with this name is present,
    // and if so, we execute the function provided
    AVM1MovieClip.pokiSDKonStopActionChildName = null;
    AVM1MovieClip.pokiSDKonStopAction = null;
    AVM1MovieClip.capStyleMapStringToInt = { 'none': 0, 'round': 1, 'square': 2 };
    AVM1MovieClip.jointStyleMapStringToInt = { 'round': 0, 'bevel': 1, 'miter': 2 };
    AVM1MovieClip.noScaleDictionary = {
        'normal': LineScaleMode.NORMAL,
        'none': LineScaleMode.NONE,
        'vertical': LineScaleMode.VERTICAL,
        'horizontal': LineScaleMode.HORIZONTAL
    };
    return AVM1MovieClip;
}(AVM1SymbolBase));
export { AVM1MovieClip };
